import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apis from '#store/api';
import { DataApiResponse, DataCollectionApiResponse } from "./types";
import { CreateUserDto } from "./users";
import { MediaFile } from "./media";

export const TENANTS_KEY = 'tenants';

export interface Tenant {
  id?: string;
  organizationName: string;
  organizationId: string;
  language?: string;
  unit?: string;
  logo?: MediaFile; 
  status?: string;
  type?: string;
}

interface UseTenantsOptions {
  enabled?: boolean;
}

interface GetTenantsParams {
  archived?: boolean;
  skip?: number;
  take?: number;
}

interface GetTenantByIdParams {
  id: string;
}

interface UpdateTenantParams {
  tenant: Tenant;
  logo?: Base64UploadFileDto;
  deleteLogo?: boolean;
}

interface UpdateTenantStatusParams {
  id: string;
  status: string;
}

interface DeleteTenantParams {
  id: string;
  password: string;
}

export interface Base64UploadFileDto {
  name: string;
  type: string; // e.g. image/jpeg
  content: string; // base64 encoded content
  referenceId: string;
  referenceType: string;
  kind: string;
}

interface CreateTenantParams {
  tenant: Tenant;
  logo?: Base64UploadFileDto;
  admin: CreateUserDto;
}

interface UpdateTenantParams {
  tenant: Tenant;
}

interface UpdateTenantDefaultUnitsParams{
  unit?: string;
  language?: string;
}

interface UpdateTenantStatusParams {
  id: string;
  status: string;
}

interface DeleteTenantParams {
  id: string;
  password: string;
}

// Fetch tenants list
export const useTenants = (params: GetTenantsParams, options: UseTenantsOptions = {}) => {
  return useQuery({
    queryKey: [TENANTS_KEY, params],
    queryFn: async () => {
      const response = await apis.tenants.getTenantsList({ params });
      return response.data as DataCollectionApiResponse<Tenant>;
    },
    enabled: options.enabled,
    refetchInterval: 60000
  });
};

// Fetch tenant by id
export const useTenantById = (params: GetTenantByIdParams) => {
  return useQuery({
    queryKey: [TENANTS_KEY, params],
    queryFn: async () => {
      const response = await apis.tenants.getTenantById({ data: params });
      return response.data as DataApiResponse<Tenant>;
    },
    enabled: !!params.id
  });
};

// Create tenant
export const useCreateTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
      mutationFn: async (params: CreateTenantParams) => {
          queryClient.invalidateQueries({ queryKey: [TENANTS_KEY] }); // reset tenant list upon tenant creation attempt
          const response = await apis.tenants.createTenant({ data: params });
          return response.data as DataApiResponse<Tenant>;
      },
      onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [TENANTS_KEY] });
      },
  });
};

// Update tenant
export const useUpdateTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
      mutationFn: async (params: UpdateTenantParams) => {
          const response = await apis.tenants.updateTenant({ data: params });
          return response.data as DataApiResponse<Tenant>;
      },
      onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [TENANTS_KEY] });
      },
  });
};

// Update tenant default units
export const useUpdateTenantDefaultUnits = () => {
  const queryClient = useQueryClient();

  return useMutation({
      mutationFn: async (params: UpdateTenantDefaultUnitsParams) => {
          const response = await apis.tenants.updateTenantDefaultUnits({ data: params });
          return response.data as DataApiResponse<Tenant>;
      },
      onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [TENANTS_KEY] });
      },
  });
};

// Update tenant status
export const useUpdateTenantStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
      mutationFn: async (params: UpdateTenantStatusParams) => {
          const response = await apis.tenants.updateTenantStatus({ data: params });
          return response.data as DataApiResponse<Tenant>;
      },
      onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [TENANTS_KEY] });
      },
  });
};

// Delete tenant
export const useDeleteTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
      mutationFn: async (params: DeleteTenantParams) => {
          const response = await apis.tenants.deleteTenant({ data: params });
          return response.data;
      },
      onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [TENANTS_KEY] });
      },  
  });
};